import React, { useCallback, useEffect, useMemo } from 'react';
import { Distribution, TransferIRADistributionReason, ValidReason } from '@tradingblock/types';
import { CashieringViewFormValues, CashieringViewProps } from '../data/useCashieringView';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { FormGroup, SelectDropdownInput, StepperInput, PercentField, CurrencyField } from '@tradingblock/components';
import { Field } from 'formik';
import {
  getDistributionDescription,
  getFormattedTransferIRADistributionReason,
} from '../../../../utilities/cashiering';
import _ from 'lodash';
import { TaxWithholdingRequirement } from './TaxWithholdingRequirement';
import { DateField } from '../../../../components/form/DateField';

export const IRADistributions: React.FC<CashieringViewProps<CashieringViewFormValues>> = props => {
  // IRA Distribution Props
  const { values, setValues } = props;
  const { transfer } = values;
  const { iraDistribution } = transfer || {};
  const cashieringProfile = useStateSelector(s => s.cashiering.cashieringProfile);
  const { iraConstraints } = cashieringProfile;
  const { distribution, federalTaxWithholdingRequirements, stateTaxWithholdingRequirements } = iraConstraints
    ? iraConstraints
    : {
        distribution: undefined,
        federalTaxWithholdingRequirements: undefined,
        stateTaxWithholdingRequirements: undefined,
      };
  const { mechanism } = transfer ? transfer : { mechanism: undefined };
  const formattedMechanism = mechanism ? mechanism.toLowerCase() : undefined;
  const [selectedReason, setSelectedReason] = React.useState<TransferIRADistributionReason | undefined>(
    iraDistribution && iraDistribution.reason ? iraDistribution.reason : undefined
  );
  const [selectedReasonObject, setSelectedReasonObject] = React.useState<ValidReason | undefined>(
    iraDistribution && iraDistribution.reason
      ? distribution &&
          distribution[formattedMechanism as keyof Distribution].validReasons.find(
            (reason: any) => reason.reason === iraDistribution.reason
          )
      : undefined
  );
  const [withholdingType, setWithholdingType] = React.useState<any>(
    iraDistribution && iraDistribution.federalTaxWithholding
      ? {
          value: iraDistribution.federalTaxWithholding.valueType,
          label: iraDistribution.federalTaxWithholding.valueType,
        }
      : undefined
  );
  const dateOfDeathRequired = selectedReasonObject ? selectedReasonObject.dateOfDeathRequired : false;

  useEffect(() => {
    // if the valueType is not set within the iraDistribution object, set the withholdingType to undefined
    if (iraDistribution && iraDistribution.federalTaxWithholding && !iraDistribution.federalTaxWithholding.valueType) {
      setWithholdingType(undefined);
    }
  }, [iraDistribution]);

  // distribution reasons will be based on the mechanism selected
  // mechanism has a value of 'Ach' 'Check' or 'Wire'
  // corresponding objects in distribution will have a key of 'ach' 'check' or 'wire'
  const distributionReasons =
    distribution && mechanism && formattedMechanism
      ? distribution[formattedMechanism as keyof Distribution]
      : undefined;

  // Distribution Reason Options
  const distributionOptions = distributionReasons
    ? distributionReasons.validReasons.map(reason => ({
        value: reason,
        label: getFormattedTransferIRADistributionReason(reason.reason as TransferIRADistributionReason),
      }))
    : [];

  let selectedDistributionValue: any;
  if (values.transfer && values.transfer.iraDistribution && values.transfer.iraDistribution.reason) {
    selectedDistributionValue = getFormattedTransferIRADistributionReason(values.transfer.iraDistribution.reason);
  }

  const distributionReasonDescription = getDistributionDescription(
    (values && values.transfer && values.transfer.iraDistribution && values.transfer.iraDistribution.reason) ||
      ('Unknown Distribution Reason' as TransferIRADistributionReason)
  );

  // withholding type options
  // if the disbursement type is FullBalance, only allow the user to select a percentage
  const withholdingTypeOptions = [
    { value: 'Percentage', label: 'Percentage' },
    { value: 'Fixed', label: 'Fixed', disabled: transfer && transfer.disbursementType === 'FullBalance' },
  ];

  // variable to determine if both federal and state tax withHoldingOption is disabled at the profile level
  const isStateWithholdingOptionDisabledForProfile = stateTaxWithholdingRequirements
    ? stateTaxWithholdingRequirements.withholdingOption === 'Disabled'
    : false;
  const isFederalWithholdingOptionDisabledForProfile = federalTaxWithholdingRequirements
    ? federalTaxWithholdingRequirements.withholdingOption === 'Disabled'
    : false;
  const isFederalAndStateTaxWithholdingOptionDisabled =
    isStateWithholdingOptionDisabledForProfile && isFederalWithholdingOptionDisabledForProfile;

  // variables to determine if the federal/state tax withholding options are enabled at the valid reason level
  const isStateWithholdingEnabledForReason = selectedReasonObject && selectedReasonObject.stateTaxWithholdingEnabled;
  const isFederalWithholdingEnabledForReason =
    selectedReasonObject && selectedReasonObject.federalTaxWithholdingEnabled;
  const isFederalAndStateTaxWithholdingEnabledForReason =
    isStateWithholdingEnabledForReason && isFederalWithholdingEnabledForReason;

  // Functions
  const onSelectDistributionReason = useCallback(
    (e, distributionReason: ValidReason) => {
      setValues({
        ...values,
        transfer: {
          ...values.transfer,
          iraDistribution: {
            ...(values.transfer && values.transfer.iraDistribution),
            reason: distributionReason.reason as TransferIRADistributionReason,
            hasStateWithholdingWaiver: false,
            federalTaxWithholding: {
              ...(values.transfer &&
                values.transfer.iraDistribution &&
                values.transfer.iraDistribution.federalTaxWithholding),
              valueType: undefined,
              value: undefined,
            },
            stateTaxWithholding: {
              ...(values.transfer &&
                values.transfer.iraDistribution &&
                values.transfer.iraDistribution.stateTaxWithholding),
              valueType: undefined,
              value: undefined,
            },
            dateOfDeath: undefined,
          },
        },
      });
      setSelectedReason(distributionReason.reason as TransferIRADistributionReason);
      setSelectedReasonObject(distributionReason);
      setWithholdingType(undefined);
    },
    [values]
  );
  const onSelectWithholdingType = useCallback(
    (e, withholdingType: any) => {
      // Update the state with the new withholding values
      setValues({
        ...values,
        transfer: {
          ...values.transfer,
          iraDistribution: {
            ...(values.transfer && values.transfer.iraDistribution),
            federalTaxWithholding: {
              ...(values.transfer &&
                values.transfer.iraDistribution &&
                values.transfer.iraDistribution.federalTaxWithholding),
              valueType: withholdingType,
              value: undefined,
            },
            stateTaxWithholding: {
              ...(values.transfer &&
                values.transfer.iraDistribution &&
                values.transfer.iraDistribution.stateTaxWithholding),
              valueType: withholdingType,
              value: undefined,
            },
          },
        },
      });

      // Update the withholding type and reset the federal and state tax withholding values
      setWithholdingType({ value: withholdingType, label: withholdingType });
    },
    [values]
  );

  const getDollarValue = (percentage: number, total: number) => {
    return ((percentage / 100) * total).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const amount = useMemo(() => {
    // amount of the transfer could be a string with or without a preceding '$' or a number
    // if it's a string, remove the '$' and convert to a number
    // always return a number
    if (values && values.transfer && values.transfer.amount) {
      if (typeof values.transfer.amount === 'string') {
        return parseFloat(values.transfer.amount.replace('$', ''));
      }
      return values.transfer.amount;
    }
  }, [values && values.transfer && values.transfer.amount]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Step 1: Remove invalid characters (only digits and a single decimal point allowed)
    newValue = newValue.replace(/[^0-9.]/g, '');

    // Step 2: Ensure there's only one decimal point
    if ((newValue.match(/\./g) || []).length > 1) {
      newValue = newValue.slice(0, -1);
    }

    // Step 3: Restrict to two decimal places if applicable
    const parts = newValue.split('.');
    if (parts[1] && parts[1].length > 2) {
      newValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    // Step 4: Apply maximum restrictions based on input type (percentage or fixed)
    if (withholdingType && withholdingType.value === 'Percentage') {
      // Limit the value to 100 for percentage
      if (parseFloat(newValue) > 100) {
        newValue = '100';
      }
    } else {
      // Limit the value to the transfer amount for fixed dollar
      if (amount && parseFloat(newValue) > amount) {
        newValue = amount.toString();
      }
    }

    // Update the value in the input field
    e.target.value = newValue;
  };

  return (
    <>
      <div className="screen-body-section">
        <p className="fields-title">IRA Distribution Information</p>
        <FormGroup>
          <SelectDropdownInput
            name="distributionReason"
            label="Distribution Reason"
            options={
              distributionOptions.length > 0
                ? distributionOptions
                : [{ value: 'No Distribution Reasons Available', label: 'No Distribution Reasons Available' }]
            }
            value={selectedDistributionValue}
            onchange={(e, distributionReason: ValidReason) => {
              onSelectDistributionReason(e, distributionReason);
            }}
            menuPlacement="auto"
          />
          {selectedReason && <p className="txt-sm mute">{distributionReasonDescription}</p>}
        </FormGroup>
      </div>
      {values.transfer && values.transfer.iraDistribution && selectedReasonObject && dateOfDeathRequired && (
        <div className="screen-body-section">
          <FormGroup>
            <p className="fields-title">Date of Death</p>
            <Field
              component={DateField}
              id="transfer.iraDistribution.dateOfDeath"
              label="Date of Death"
              type="date"
              errorAfterSubmit={true}
            />
          </FormGroup>
        </div>
      )}
      {values.transfer &&
        values.transfer.iraDistribution &&
        selectedReason &&
        !isFederalAndStateTaxWithholdingOptionDisabled &&
        (isStateWithholdingEnabledForReason || isFederalWithholdingEnabledForReason) && (
          <div className="screen-body-section">
            <FormGroup>
              {/*Input for selecting if the user would like to apply a percentage or fixed value for federal/state tax withholding purposes */}
              <p className="fields-title">Withholding Type</p>
              <StepperInput
                name="withholdingType"
                label="Withholding Type"
                options={withholdingTypeOptions}
                onchange={(e: any, withholdingType: any) => {
                  onSelectWithholdingType(e, withholdingType);
                }}
                value={withholdingType && withholdingType.value}
              />
            </FormGroup>
            {/*Federal and State Tax Withholding Inputs - Only display if the valid reason allows for federal/state tax withholding */}

            <FormGroup>
              <div className="row">
                {isFederalWithholdingEnabledForReason && (
                  <>
                    <div className="col">
                      {withholdingType && withholdingType.value === 'Percentage' && (
                        <Field
                          component={PercentField}
                          id="transfer.iraDistribution.federalTaxWithholding.value"
                          label={`Federal Tax Withholding (%) - ${getDollarValue(
                            (values &&
                              values.transfer &&
                              values.transfer.iraDistribution &&
                              values.transfer.iraDistribution.federalTaxWithholding &&
                              values.transfer.iraDistribution.federalTaxWithholding.value) ||
                              0,
                            amount || 0
                          )}`}
                          disabled={
                            (federalTaxWithholdingRequirements &&
                              federalTaxWithholdingRequirements.withholdingOption === 'Disabled') ||
                            !withholdingType
                          }
                        />
                      )}
                      {withholdingType && withholdingType.value === 'Fixed' && (
                        <Field
                          component={CurrencyField}
                          id="transfer.iraDistribution.federalTaxWithholding.value"
                          // label should include the percentage of the transfer amount

                          label={`Federal Tax Withholding - (${Number(
                            (
                              (((values &&
                                values.transfer &&
                                values.transfer.iraDistribution &&
                                values.transfer.iraDistribution.federalTaxWithholding &&
                                values.transfer.iraDistribution.federalTaxWithholding.value) ||
                                0) /
                                (amount || 1)) *
                              100
                            ).toFixed(4)
                          ) || 0}%)`}
                          disabled={
                            (federalTaxWithholdingRequirements &&
                              federalTaxWithholdingRequirements.withholdingOption === 'Disabled') ||
                            !withholdingType
                          }
                        />
                      )}
                      <TaxWithholdingRequirement
                        type="federal"
                        withholdingOption={
                          federalTaxWithholdingRequirements && federalTaxWithholdingRequirements.withholdingOption
                        }
                        requiredMinimum={
                          federalTaxWithholdingRequirements && federalTaxWithholdingRequirements.requiredMinimum
                        }
                        requiredMinimumPercent={
                          federalTaxWithholdingRequirements && federalTaxWithholdingRequirements.requiredMinimumPercent
                        }
                        values={values}
                      />
                    </div>
                  </>
                )}
                {!isFederalWithholdingEnabledForReason && (
                  <div className="col">
                    <p className="fields-title">Federal Tax Withholding is Disabled</p>
                  </div>
                )}

                {isStateWithholdingEnabledForReason && (
                  <>
                    <div className="col">
                      {withholdingType && withholdingType.value === 'Percentage' && (
                        <Field
                          component={PercentField}
                          id="transfer.iraDistribution.stateTaxWithholding.value"
                          label={`State Tax Withholding (%) - ${getDollarValue(
                            (values &&
                              values.transfer &&
                              values.transfer.iraDistribution &&
                              values.transfer.iraDistribution.stateTaxWithholding &&
                              values.transfer.iraDistribution.stateTaxWithholding.value) ||
                              0,
                            amount || 0
                          )}`}
                          disabled={
                            (stateTaxWithholdingRequirements &&
                              stateTaxWithholdingRequirements.withholdingOption === 'Disabled') ||
                            !withholdingType
                          }
                        />
                      )}
                      {withholdingType && withholdingType.value === 'Fixed' && (
                        <Field
                          component={CurrencyField}
                          id="transfer.iraDistribution.stateTaxWithholding.value"
                          label={`State Tax Withholding - (${Number(
                            (
                              (((values &&
                                values.transfer &&
                                values.transfer.iraDistribution &&
                                values.transfer.iraDistribution.stateTaxWithholding &&
                                values.transfer.iraDistribution.stateTaxWithholding.value) ||
                                0) /
                                (amount || 1)) *
                              100
                            ).toFixed(4)
                          ) || 0}%)`}
                          disabled={
                            (stateTaxWithholdingRequirements &&
                              stateTaxWithholdingRequirements.withholdingOption === 'Disabled') ||
                            !withholdingType
                          }
                        />
                      )}

                      <TaxWithholdingRequirement
                        type="state"
                        withholdingOption={
                          stateTaxWithholdingRequirements && stateTaxWithholdingRequirements.withholdingOption
                        }
                        requiredMinimum={
                          stateTaxWithholdingRequirements && stateTaxWithholdingRequirements.requiredMinimum
                        }
                        requiredMinimumPercent={
                          stateTaxWithholdingRequirements && stateTaxWithholdingRequirements.requiredMinimumPercent
                        }
                        values={values}
                      />
                    </div>
                  </>
                )}

                {!isStateWithholdingEnabledForReason && (
                  <div className="col">
                    <p className="fields-title">State Tax Withholding is Disabled</p>
                  </div>
                )}
              </div>
            </FormGroup>
          </div>
        )}
      {values.transfer &&
        values.transfer.iraDistribution &&
        selectedReason &&
        isFederalAndStateTaxWithholdingOptionDisabled && (
          <div className="screen-body-section">
            <p className="fields-title">Federal and State Tax Withholding is Disabled</p>
          </div>
        )}
    </>
  );
};
